/**
 * Owner Dashboard redesign (AB#2206) — the first viewport: Needs attention,
 * Today's appointments and Today at a glance. Rendering only: figures come from
 * useOwnerToday, row actions from the backend's allowedTransitions / canMarkPaid.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import BookingActions from '../../../components/BookingDetail/BookingActions';
import BookingStatusPill from '../../../components/BookingDetail/BookingStatusPill';
import { bookingStatusColors } from '../../../lib/bookingLifecycle';
import { formatDurationMinutes } from '../../../lib/humanDuration';
import { fill, isolate, timeRange } from '../../../lib/staffDisplay';
import { addMinutesHHmm } from '../../../components/BookingDetail/staffBooking';
import type { SalonOwnerAppointmentRowDto } from '../../../services/appointmentsList';
import {
    hhmmToMinutes, isPastRow, nextRowAction, nowLineIndex, TODAY_ROW_LIMIT,
    type RosterEntry, type TodaySummary,
} from './dashboardModel';
import { formatMoney, formatShortDay, initials } from './dashboardFormat';
import s from './Dashboard.module.css';

type TFn = (key: string) => string;

const Avatar: React.FC<{ name: string; photoUrl?: string | null; muted?: boolean }> = ({ name, photoUrl, muted }) => (
    photoUrl
        ? <img className={s.avatar} src={photoUrl} alt="" />
        : <span className={`${s.avatar} ${muted ? s.avatarMuted : ''}`} aria-hidden="true">{initials(name)}</span>
);

/* ── Needs attention ─────────────────────────────────────────────── */

export interface AttentionProps {
    loading: boolean;
    pendingCount: number;
    pendingEarliest: SalonOwnerAppointmentRowDto | null;
    unpaidCount: number;
    unpaidAmount: number;
    unpaidPartial: boolean;
    currency: string;
    next: SalonOwnerAppointmentRowDto | null;
    nowHHmm: string;
    /** `&branchId=N` when one branch is selected, so the linked list matches the count. */
    branchQuery: string;
    onOpen: (row: SalonOwnerAppointmentRowDto) => void;
}

export const AttentionStrip: React.FC<AttentionProps> = ({
    loading, pendingCount, pendingEarliest, unpaidCount, unpaidAmount, unpaidPartial, currency, next, nowHHmm, branchQuery, onOpen,
}) => {
    const { t, language } = useLanguage();

    if (loading) {
        return (
            <section className={s.attention} aria-label={t('odash.attn.region')} aria-busy="true">
                {[0, 1, 2].map(i => <div key={i} className={`${s.tile} ${s.skeleton}`} />)}
            </section>
        );
    }

    if (pendingCount === 0 && unpaidCount === 0 && !next) {
        return (
            <section className={s.allClear} aria-label={t('odash.attn.region')} data-testid="dash-all-clear">
                <span className={s.allClearDot} aria-hidden="true" />
                <div>
                    <p className={s.allClearTitle}>{t('odash.attn.allClear')}</p>
                    <p className={s.muted}>{t('odash.attn.allClearSub')}</p>
                </div>
            </section>
        );
    }

    const wait = next ? (hhmmToMinutes(next.branchLocalStartTime) ?? 0) - (hhmmToMinutes(nowHHmm) ?? 0) : 0;
    const amount = formatMoney(unpaidAmount, currency) + (unpaidPartial ? '+' : '');

    return (
        <section className={s.attention} aria-label={t('odash.attn.region')}>
            <Link
                to={`/appointments?tab=pending${branchQuery}`}
                className={`${s.tile} ${pendingCount > 0 ? s.tileWarn : s.tileQuiet}`}
                data-testid="dash-attn-pending"
            >
                <span className={s.tileNum}>{pendingCount}</span>
                <span className={s.tileBody}>
                    <span className={s.tileTitle}>{pendingCount > 0 ? t('odash.attn.pending.title') : t('odash.attn.pending.none')}</span>
                    {pendingEarliest && (
                        <span className={s.tileSub}>
                            {fill(t('odash.attn.pending.earliest'), {
                                when: `${formatShortDay(pendingEarliest.appointmentDate, language)} · ${isolate(pendingEarliest.branchLocalStartTime)}`,
                            })}
                        </span>
                    )}
                </span>
                {pendingCount > 0 && <span className={s.tileCta}>{t('odash.attn.pending.cta')}</span>}
            </Link>

            <Link
                to={`/appointments?tab=past&payment=unpaid&status=Completed${branchQuery}`}
                className={`${s.tile} ${unpaidCount > 0 ? s.tileDanger : s.tileQuiet}`}
                data-testid="dash-attn-unpaid"
            >
                <span className={s.tileNum}>{unpaidCount}</span>
                <span className={s.tileBody}>
                    <span className={s.tileTitle}>{unpaidCount > 0 ? t('odash.attn.unpaid.title') : t('odash.attn.unpaid.none')}</span>
                    {unpaidCount > 0 && <span className={s.tileSub}>{fill(t('odash.attn.unpaid.amount'), { amount })}</span>}
                </span>
                {unpaidCount > 0 && <span className={s.tileCta}>{t('booking.action.markPaid')}</span>}
            </Link>

            {next ? (
                <button type="button" className={`${s.tile} ${s.tileNext}`} onClick={() => onOpen(next)} data-testid="dash-attn-next">
                    <span className={`${s.tileNum} ${s.tileTime}`}>{isolate(next.branchLocalStartTime)}</span>
                    <span className={s.tileBody}>
                        <span className={s.tileTitle}>
                            {fill(t('odash.attn.next.title'), {
                                customer: isolate(next.clientDisplayName || t('booking.detail.walkIn')),
                                service: isolate(next.serviceName),
                            })}
                        </span>
                        <span className={s.tileSub}>
                            {fill(t('odash.attn.next.with'), { staff: isolate(next.staffDisplayName || '—'), branch: isolate(next.branchName) })}
                            {' · '}
                            {wait <= 0 ? t('odash.attn.next.now') : fill(t('odash.attn.next.in'), { wait: formatDurationMinutes(wait, t) })}
                        </span>
                    </span>
                    <span className={s.tileCta}>{t('odash.attn.next.cta')}</span>
                </button>
            ) : (
                <div className={`${s.tile} ${s.tileQuiet}`} data-testid="dash-attn-next">
                    <span className={s.tileBody}><span className={s.tileTitle}>{t('odash.attn.next.none')}</span></span>
                </div>
            )}
        </section>
    );
};

/* ── Today's appointments ────────────────────────────────────────── */

export interface TodayListProps {
    rows: SalonOwnerAppointmentRowDto[];
    hasMore: boolean;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    nowHHmm: string;
    staffCount: number;
    calendarHref: string;
    branchQuery: string;
    onOpen: (row: SalonOwnerAppointmentRowDto) => void;
    onMarkPaid: (row: SalonOwnerAppointmentRowDto) => void;
    onChanged: () => void;
}

const TodayRow: React.FC<{
    row: SalonOwnerAppointmentRowDto;
    past: boolean;
    t: TFn;
    onOpen: (row: SalonOwnerAppointmentRowDto) => void;
    onMarkPaid: (row: SalonOwnerAppointmentRowDto) => void;
    onChanged: () => void;
}> = ({ row, past, t, onOpen, onMarkPaid, onChanged }) => {
    const action = nextRowAction(row);
    const end = addMinutesHHmm(row.branchLocalStartTime, row.durationMinutes);
    const tone = bookingStatusColors(row.status);
    return (
        <li className={`${s.row} ${past ? s.rowPast : ''}`} data-testid="dash-today-row">
            <button
                type="button"
                className={s.rowOpen}
                onClick={() => onOpen(row)}
            >
                <span className={s.rowTime}>
                    <span className={s.rowStart}>{isolate(row.branchLocalStartTime)}</span>
                    <span className={s.rowDur}>{formatDurationMinutes(row.durationMinutes, t)}</span>
                </span>
                <span className={s.rowBar} style={{ background: tone.fg }} aria-hidden="true" />
                <span className={s.rowWho}>
                    <span className={s.rowCustomer}><bdi>{row.clientDisplayName || t('booking.detail.walkIn')}</bdi></span>
                    <span className={s.rowService}>
                        <bdi>{row.serviceName}</bdi>
                        <span aria-hidden="true"> · </span>
                        <bdi>{row.branchName}</bdi>
                    </span>
                </span>
                <span className={s.rowStaff}>
                    <Avatar name={row.staffDisplayName} photoUrl={row.staffAvatarUrl} />
                    <bdi className={s.ellipsis}>{row.staffDisplayName || '—'}</bdi>
                </span>
                <span className={s.rowStatus}>
                    <BookingStatusPill status={row.status} cancellationSource={row.cancellationSource} />
                </span>
            </button>
            <span className={s.rowAction}>
                {action?.kind === 'transition' && (
                    <BookingActions
                        // A new key per status: after a step succeeds the control
                        // remounts and offers the NEXT backend-allowed step.
                        key={`${row.id}-${row.status}`}
                        appointmentId={row.id}
                        status={row.status}
                        allowedTransitions={row.allowedTransitions}
                        only={[action.target]}
                        compact
                        onChanged={onChanged}
                    />
                )}
                {action?.kind === 'markPaid' && (
                    <button type="button" className={s.btnSecondary} onClick={() => onMarkPaid(row)}>
                        {t('booking.action.markPaid')}
                    </button>
                )}
            </span>
            {/* Hidden end time keeps the visible column compact; screen readers get the range. */}
            <span className={s.srOnly}>{timeRange(row.branchLocalStartTime, end)}</span>
        </li>
    );
};

/** True when rows exist beyond those shown (the "now" line would then sit among hidden rows). */
function hasMoreHidden(total: number, hasMore: boolean): boolean {
    return hasMore || total > TODAY_ROW_LIMIT;
}

export const TodayAppointments: React.FC<TodayListProps> = ({
    rows, hasMore, loading, error, onRetry, nowHHmm, staffCount, calendarHref, branchQuery, onOpen, onMarkPaid, onChanged,
}) => {
    const { t } = useLanguage();
    const shown = rows.slice(0, TODAY_ROW_LIMIT);
    const nowAt = nowLineIndex(shown, nowHHmm);
    const total = rows.length;

    return (
        <section className={`${s.card} ${s.todayCard}`} aria-labelledby="dash-today-title">
            <header className={s.cardHead}>
                <div className={s.cardHeadText}>
                    <h2 id="dash-today-title" className={s.cardTitle}>{t('odash.today.title')}</h2>
                    {!loading && !error && total > 0 && (
                        <span className={s.cardSub}>
                            {fill(t('odash.today.sub'), { count: total + (hasMore ? '+' : ''), staff: staffCount })}
                        </span>
                    )}
                </div>
                <Link to={calendarHref} className={s.link}>{t('odash.today.openCalendar')}</Link>
            </header>

            {loading && (
                <ul className={s.rows} aria-busy="true">
                    {[0, 1, 2, 3].map(i => <li key={i} className={`${s.rowSkeleton} ${s.skeleton}`} />)}
                </ul>
            )}

            {!loading && error && (
                <div className={s.cardError} role="alert">
                    <span>{t('odash.today.loadFailed')}</span>
                    <button type="button" className={s.btnSecondary} onClick={onRetry}>{t('odash.retry')}</button>
                </div>
            )}

            {!loading && !error && total === 0 && (
                <div className={s.empty} data-testid="dash-today-empty">
                    <p className={s.emptyTitle}>{t('odash.today.empty')}</p>
                    <p className={s.muted}>{t('odash.today.emptySub')}</p>
                </div>
            )}

            {!loading && !error && total > 0 && (
                <ul className={s.rows}>
                    {shown.map((row, i) => (
                        <React.Fragment key={row.id}>
                            {i === nowAt && i > 0 && (
                                <li className={s.nowLine} aria-hidden="true"><span>{t('odash.today.now')} · {isolate(nowHHmm)}</span></li>
                            )}
                            <TodayRow
                                row={row}
                                past={isPastRow(row, nowHHmm)}
                                t={t}
                                onOpen={onOpen}
                                onMarkPaid={onMarkPaid}
                                onChanged={onChanged}
                            />
                        </React.Fragment>
                    ))}
                    {nowAt === shown.length && !hasMoreHidden(total, hasMore) && (
                        <li className={s.nowLine} aria-hidden="true"><span>{t('odash.today.now')} · {isolate(nowHHmm)}</span></li>
                    )}
                </ul>
            )}

            {!loading && !error && (total > TODAY_ROW_LIMIT || hasMore) && (
                <footer className={s.cardFoot}>
                    <Link to={`/appointments?tab=today${branchQuery}`} className={s.link}>
                        {fill(t('odash.today.more'), { count: total + (hasMore ? '+' : '') })}
                    </Link>
                </footer>
            )}
        </section>
    );
};

/* ── Today at a glance ───────────────────────────────────────────── */

/** People shown on shift before "+N more" — the glance card stays compact. */
const ROSTER_LIMIT = 6;

/** One line per person: earliest start – latest end, their branches, combined booked share. */
function personShift(r: RosterEntry, separator: string) {
    const byStart = [...r.shifts].sort((a, b) => (hhmmToMinutes(a.start) ?? 0) - (hhmmToMinutes(b.start) ?? 0));
    const byEnd = [...r.shifts].sort((a, b) => (hhmmToMinutes(b.end) ?? 0) - (hhmmToMinutes(a.end) ?? 0));
    const minutes = r.shifts.reduce((sum, sh) => sum + ((hhmmToMinutes(sh.end) ?? 0) - (hhmmToMinutes(sh.start) ?? 0)), 0);
    const booked = r.shifts.reduce((sum, sh) => sum + sh.load * ((hhmmToMinutes(sh.end) ?? 0) - (hhmmToMinutes(sh.start) ?? 0)), 0);
    return {
        start: byStart[0]?.start ?? '',
        end: byEnd[0]?.end ?? '',
        branches: Array.from(new Set(r.shifts.map(sh => sh.branchName))).join(separator),
        loadPct: minutes > 0 ? Math.round((booked / minutes) * 100) : 0,
    };
}

export interface GlanceProps {
    summary: TodaySummary;
    currency: string;
    loading: boolean;
    roster: RosterEntry[];
    rosterLoading: boolean;
    rosterError: boolean;
    multiBranch: boolean;
}

export const TodayGlance: React.FC<GlanceProps> = ({ summary, currency, loading, roster, rosterLoading, rosterError, multiBranch }) => {
    const { t, language } = useLanguage();
    const pct = summary.expected > 0 ? Math.round((summary.collected / summary.expected) * 100) : 0;
    const collectedW = summary.expected > 0 ? Math.min(100, (summary.collected / summary.expected) * 100) : 0;
    const toCollectW = summary.expected > 0 ? Math.min(100 - collectedW, (summary.toCollect / summary.expected) * 100) : 0;
    const working = roster.filter(r => r.state === 'working');
    const leave = roster.filter(r => r.state === 'leave');
    const off = roster.filter(r => r.state === 'off');
    const v = (x: React.ReactNode) => (loading ? '—' : x);

    return (
        <section className={`${s.card} ${s.glanceCard}`} aria-labelledby="dash-glance-title">
            <header className={s.cardHead}>
                <h2 id="dash-glance-title" className={s.cardTitle}>{t('odash.glance.title')}</h2>
            </header>
            <dl className={s.glance}>
                <div className={s.stat}>
                    <dt>{t('odash.glance.bookings')}</dt>
                    <dd className={s.statValue}>{v(summary.active)}</dd>
                    {!loading && <dd className={s.statSub}>{fill(t('odash.glance.bookingsSub'), { pending: summary.pending, ended: summary.ended })}</dd>}
                </div>
                <div className={s.stat}>
                    <dt>{t('odash.glance.expected')}</dt>
                    <dd className={s.statValue}>{v(formatMoney(summary.expected, currency))}</dd>
                    {!loading && <dd className={s.statSub}>{t('odash.glance.expectedSub')}</dd>}
                </div>
                <div className={s.stat}>
                    <dt>{t('odash.glance.collected')}</dt>
                    <dd className={`${s.statValue} ${s.valueOk}`}>{v(formatMoney(summary.collected, currency))}</dd>
                    {!loading && <dd className={s.statSub}>{fill(t('odash.glance.collectedSub'), { pct })}</dd>}
                </div>
                <div className={s.stat}>
                    <dt>{t('odash.glance.toCollect')}</dt>
                    <dd className={`${s.statValue} ${summary.toCollect > 0 ? s.valueDanger : ''}`}>{v(formatMoney(summary.toCollect, currency))}</dd>
                    {!loading && <dd className={s.statSub}>{fill(t('odash.glance.toCollectSub'), { count: summary.toCollectCount })}</dd>}
                </div>
            </dl>
            {!loading && summary.expected > 0 && (
                <div
                    className={s.meter}
                    role="img"
                    aria-label={fill(t('odash.glance.meter'), {
                        collected: formatMoney(summary.collected, currency),
                        expected: formatMoney(summary.expected, currency),
                    })}
                >
                    <span className={s.meterOk} style={{ inlineSize: `${collectedW}%` }} />
                    <span className={s.meterDue} style={{ inlineSize: `${toCollectW}%` }} />
                </div>
            )}

            <div className={s.shift}>
                <h3 className={s.overline}>{t('odash.shift.title')}</h3>
                {rosterLoading && <div className={`${s.rowSkeleton} ${s.skeleton}`} />}
                {!rosterLoading && rosterError && <p className={s.muted} role="alert">{t('odash.shift.loadFailed')}</p>}
                {!rosterLoading && !rosterError && working.length === 0 && leave.length === 0 && (
                    <p className={s.muted}>{t('odash.shift.none')}</p>
                )}
                {!rosterLoading && !rosterError && (
                    <ul className={s.roster}>
                        {working.slice(0, ROSTER_LIMIT).map(r => {
                            const view = personShift(r, language === 'ar' ? '، ' : ', ');
                            return (
                                <li key={r.staffId} className={s.person} data-testid="dash-shift-working">
                                    <Avatar name={r.name} photoUrl={r.photoUrl} />
                                    <span className={s.personName}>
                                        <bdi>{r.name}</bdi>
                                        {multiBranch && <span className={s.muted}> · <bdi>{view.branches}</bdi></span>}
                                    </span>
                                    <span className={s.personHours}>{timeRange(view.start, view.end)}</span>
                                    <span
                                        className={s.load}
                                        role="img"
                                        aria-label={fill(t('odash.shift.load'), { pct: view.loadPct })}
                                    >
                                        <i style={{ inlineSize: `${view.loadPct}%` }} />
                                    </span>
                                </li>
                            );
                        })}
                        {working.length > ROSTER_LIMIT && (
                            <li className={`${s.person} ${s.personOff}`}>
                                <span className={`${s.avatar} ${s.avatarMuted}`} aria-hidden="true">+{working.length - ROSTER_LIMIT}</span>
                                <span className={s.personName}>{fill(t('odash.shift.moreWorking'), { count: working.length - ROSTER_LIMIT })}</span>
                            </li>
                        )}
                        {leave.map(r => (
                            <li key={`leave-${r.staffId}`} className={`${s.person} ${s.personOff}`}>
                                <Avatar name={r.name} photoUrl={null} muted />
                                <span className={s.personName}><bdi>{r.name}</bdi></span>
                                <span className={s.personHours}>{t('odash.shift.leave')}</span>
                            </li>
                        ))}
                        {off.length > 0 && (
                            <li className={`${s.person} ${s.personOff}`}>
                                <span className={`${s.avatar} ${s.avatarMuted}`} aria-hidden="true">+{off.length}</span>
                                <span className={s.personName}>{fill(t('odash.shift.notWorking'), { count: off.length })}</span>
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </section>
    );
};
